<template>
    <Card class="panel upload-panel">
        <template #title>
            <div class="panel-head">
                <span class="panel-icon"><i class="pi pi-cloud-upload" /></span>

                <span class="H6-Medium panel-head-title">
                    {{ bulkMode ? 'Bulk upload papers' : 'Upload past paper' }}
                </span>

                <div class="mode-toggle" role="tablist" aria-label="Upload mode">
                    <button v-for="mode in uploadModes" :key="mode.value" type="button" role="tab"
                        class="mode-btn" :class="{ 'is-active': uploadMode === mode.value }"
                        :aria-selected="uploadMode === mode.value" @click="setUploadMode(mode.value)">
                        <i :class="mode.icon" />
                        <span>{{ mode.label }}</span>
                    </button>
                </div>

                <span class="Caption panel-head-caption">
                    {{ bulkMode ? 'Many papers at once, one per PDF' : 'One paper with all of its PDFs' }}
                    — up to {{ maxFileSizeMb }} MB per file.
                </span>
            </div>
        </template>

        <template #content>
            <div class="panel-body">
                <section class="form-section">
                    <div class="section-head">
                        <span class="section-title">Paper details</span>
                    </div>

                    <div class="field-grid">
                        <div class="field field-wide" v-if="!bulkMode">
                            <label for="name" class="field-label">Paper name</label>
                            <InputText id="name" v-model="form.name" placeholder="e.g. O/L Mathematics 2023"
                                class="w-full" size="small" variant="filled" />
                        </div>

                        <!-- Same grid cell as the name field, so the form does not reflow. -->
                        <div class="field field-wide" v-else>
                            <span class="field-label">Paper name</span>
                            <div class="mode-note">
                                <i class="pi pi-info-circle" />
                                <span>Each paper is named after its file, minus the extension. Rename any of them later from the paper library.</span>
                            </div>
                        </div>

                        <div class="field">
                            <label for="grade" class="field-label">Grade</label>
                            <Select id="grade" v-model="form.grade" :options="gradeOptions"
                                :loading="isLoadingGrades" optionLabel="name" placeholder="Select grade"
                                class="w-full" size="small" variant="filled" />
                        </div>

                        <div class="field">
                            <label for="subject" class="field-label">Subject</label>
                            <Select id="subject" v-model="form.subject" :options="availableUploadSubjects"
                                optionLabel="name"
                                :disabled="!form.grade || availableUploadSubjects.length === 0"
                                placeholder="Select subject" class="w-full" size="small" variant="filled"
                                filter />
                        </div>

                        <div class="field">
                            <label for="year" class="field-label">
                                Year <span class="optional-hint">optional</span>
                            </label>
                            <Select id="year" v-model="form.year" :options="yearChoices"
                                placeholder="Select year" class="w-full" size="small" variant="filled"
                                filter show-clear />
                        </div>

                        <!-- Language medium applies to the local syllabus only. Hidden for
                             London/Edexcel/Cambridge, which the backend rejects anyway. -->
                        <div class="field" v-if="mediumApplies">
                            <label for="medium" class="field-label">
                                Medium <span class="optional-hint">optional</span>
                            </label>
                            <Select id="medium" v-model="form.medium" :options="mediumChoices"
                                optionLabel="label" optionValue="value" placeholder="Select medium"
                                class="w-full" size="small" variant="filled" show-clear />
                        </div>

                        <div class="field" :class="{ 'field-wide': !mediumApplies }">
                            <label for="term" class="field-label">
                                Term <span class="optional-hint">optional</span>
                            </label>
                            <Select id="term" v-model="form.term" :options="termList" optionLabel="name"
                                optionValue="code" placeholder="Select term" class="w-full" size="small"
                                variant="filled" show-clear />
                            <!-- <small class="field-hint">Public exam papers have no term.</small> -->
                        </div>
                    </div>
                </section>

                <section class="form-section">
                    <div class="section-head">
                        <span class="section-title">Attachments</span>
                        <span class="section-hint">PDF only</span>
                    </div>

                    <div class="drop-stack">
                        <div class="drop-block">
                            <div class="drop-block-head">
                                <span class="drop-block-title">
                                    Past paper PDFs <span class="required-hint">required</span>
                                </span>
                            </div>
                            <PaperFileDrop ref="paperUploadRef" v-model="files.paper" name="paperFiles"
                                title="Add the question papers" icon="pi pi-file-pdf"
                                :maxFileSize="MAX_FILE_SIZE" />
                        </div>

                        <div class="drop-block" v-if="!bulkMode">
                            <div class="drop-block-head">
                                <span class="drop-block-title">
                                    Marking schemes <span class="optional-hint">optional</span>
                                </span>
                            </div>
                            <PaperFileDrop ref="markingSchemeUploadRef" v-model="files.markingScheme"
                                name="markingSchemeFiles" title="Add marking schemes"
                                icon="pi pi-check-square" :maxFileSize="MAX_FILE_SIZE" />
                            <!-- <small class="field-hint">Marking schemes are not shown to students.</small> -->
                        </div>

                        <div class="drop-block" v-if="!bulkMode">
                            <div class="drop-block-head">
                                <span class="drop-block-title">
                                    Answer sheets <span class="optional-hint">optional</span>
                                </span>
                            </div>
                            <PaperFileDrop ref="answerSheetUploadRef" v-model="files.answerSheet"
                                name="answerSheetFiles" title="Add answer sheets"
                                icon="pi pi-pencil" :maxFileSize="MAX_FILE_SIZE" />
                            <!-- <small class="field-hint">
                                Never shown to students. Upload as many as the paper needs.
                            </small> -->
                        </div>

                        <!-- Bulk cannot match a marking scheme to a paper by filename, so it takes
                             question papers only and the rest go on afterwards. -->
                        <div class="drop-block" v-else>
                            <div class="mode-note">
                                <i class="pi pi-info-circle" />
                                <span>Marking schemes and answer sheets can be added later from the paper library</span>
                            </div>
                        </div>
                    </div>
                </section>

                <div class="panel-footer">
                    <span class="upload-summary" :class="{ 'is-blocking': !files.paper.length }">
                        <i :class="files.paper.length ? 'pi pi-paperclip' : 'pi pi-info-circle'" />
                        <template v-if="files.paper.length">
                            {{ selectedFileCount }} file{{ selectedFileCount === 1 ? '' : 's' }} ready ·
                            {{ formatFileSize(selectedFileSize) }}
                            <template v-if="bulkMode">
                                · {{ files.paper.length }} paper{{ files.paper.length === 1 ? '' : 's' }}
                                will be created
                            </template>
                        </template>
                        <template v-else>Attach at least one past paper PDF.</template>
                    </span>
                    <Button :label="submitLabel" icon="pi pi-upload" size="small"
                        class="accent-button create-button" :loading="isSaving" :disabled="!canSubmit"
                        @click="createPaperEntry" />
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup>
import { ref, reactive, computed, watch, defineEmits } from 'vue'
import PaperFileDrop from '@/components/dashboard/admin/pastPapers/PaperFileDrop.vue'
import { Button, Card, Select } from 'primevue'
import InputText from 'primevue/inputtext';
import { notifyError, notifySuccess, notifyWarn } from '@/utils/notification';
import { createPaper, bulkCreatePapers } from '@/api/papers';
import { formatFileSize, isLocalSyllabusGrade, PAPER_MEDIUM_OPTIONS } from '@/utils/paperFiles';
import { termList, gradeOptions, isLoadingGrades } from '@/utils/options';

// The library reloads on this — the panel does not hold the list itself.
const emit = defineEmits(['created']);

// Matches the 30 MB multer limit on POST /past-papers/admin.
const MAX_FILE_SIZE = 30000000;
const maxFileSizeMb = MAX_FILE_SIZE / 1000000;

const mediumChoices = PAPER_MEDIUM_OPTIONS;

const form = reactive({
    name: '',
    grade: null,
    subject: null,
    year: null,
    medium: null,
    term: null,
});

const files = reactive({ paper: [], markingScheme: [], answerSheet: [] });

/* Upload mode — single paper with its attachments, or one paper per file. */

const uploadModes = [
    { label: 'Single', value: 'single', icon: 'pi pi-file' },
    { label: 'Bulk', value: 'bulk', icon: 'pi pi-clone' },
];

const uploadMode = ref('single');
const bulkMode = computed(() => uploadMode.value === 'bulk');

// Switching to bulk drops any marking schemes or answer sheets already picked — bulk cannot match
// them to a paper, and silently discarding them at submit time would be worse.
const setUploadMode = (mode) => {
    if (uploadMode.value === mode) return;
    uploadMode.value = mode;
    if (bulkMode.value) {
        files.markingScheme = [];
        files.answerSheet = [];
        markingSchemeUploadRef.value?.clear();
        answerSheetUploadRef.value?.clear();
    }
};

const paperUploadRef = ref(null);
const markingSchemeUploadRef = ref(null);
const answerSheetUploadRef = ref(null);
const isSaving = ref(false);

// The current year is the newest a paper can be, and one going back further than this is not
// something the library holds.
const yearChoices = computed(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 40 }, (_, i) => current - i);
});

const availableUploadSubjects = computed(() => form.grade?.subjects || []);

const mediumApplies = computed(() => !form.grade || isLocalSyllabusGrade(form.grade.name));

// A paper entry is no use without the paper itself, so at least one question paper PDF is required.
// Marking schemes and answer sheets stay optional and can be added later.
const canSubmit = computed(() =>
    (bulkMode.value || !!form.name.trim()) && !!form.grade && !!form.subject
    && files.paper.length > 0 && !isSaving.value
);

const submitLabel = computed(() => {
    if (!bulkMode.value) return 'Create paper';
    const n = files.paper.length;
    return n > 1 ? `Create ${n} papers` : 'Create paper';
});

/* Footer summary of what is about to be uploaded — display only. */

const selectedFiles = computed(() => [...files.paper, ...files.markingScheme, ...files.answerSheet]);

const selectedFileCount = computed(() => selectedFiles.value.length);

const selectedFileSize = computed(() => selectedFiles.value.reduce((total, file) => total + (file.size || 0), 0));

watch(() => form.grade, () => {
    form.subject = null;
    // A medium left over from a local grade would be rejected by the backend on a foreign one.
    if (!mediumApplies.value) form.medium = null;
});

const clearUploadForm = () => {
    form.name = '';
    form.grade = null;
    form.subject = null;
    form.year = null;
    form.medium = null;
    form.term = null;
    files.paper = [];
    files.markingScheme = [];
    files.answerSheet = [];
    paperUploadRef.value?.clear();
    markingSchemeUploadRef.value?.clear();
    answerSheetUploadRef.value?.clear();
};

// Shared by both modes. Bulk sends no name — each paper is named after its file server-side.
const buildUploadFormData = () => {
    const formData = new FormData();
    if (!bulkMode.value) formData.append('name', form.name.trim());
    formData.append('grade', form.grade.code);
    formData.append('subject', form.subject.code);
    if (form.year) formData.append('year', form.year);
    if (form.medium) formData.append('medium', form.medium);
    if (form.term) formData.append('term', form.term);

    files.paper.forEach(file => formData.append('paperFiles', file));

    if (!bulkMode.value) {
        files.markingScheme.forEach(file => formData.append('markingSchemeFiles', file));
        files.answerSheet.forEach(file => formData.append('answerSheetFiles', file));
    }

    return formData;
};

const createPaperEntry = async () => {
    if (!canSubmit.value) {
        notifyWarn(
            "Missing details",
            bulkMode.value
                ? "Grade, subject and at least one past paper PDF are required."
                : "Name, grade, subject and at least one past paper PDF are required."
        );
        return;
    }

    isSaving.value = true;
    try {
        if (bulkMode.value) {
            const res = await bulkCreatePapers(buildUploadFormData());
            const { summary, failed } = res.data?.data || {};

            notifySuccess("Created!", `${summary?.created ?? 0} paper(s) created.`);
            // A partial failure still returns 201, so the skipped files are reported separately.
            if (failed?.length) {
                notifyWarn(`${failed.length} file(s) skipped`, failed.map(f => f.fileName).join(', '));
            }
        } else {
            await createPaper(buildUploadFormData());
            const total = files.paper.length + files.markingScheme.length + files.answerSheet.length;
            notifySuccess("Created!", `Paper created with ${total} file(s).`);
        }

        clearUploadForm();
        emit('created');
    } catch (err) {
        notifyError("Error", err?.response?.data?.message || "Failed to upload. Please try again.");
    } finally {
        isSaving.value = false;
    }
};
</script>

<style scoped>
/* ---------- Panel ---------- */

.panel {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border: 1px solid var(--black-50);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    overflow: hidden;
}

/* The card internals have to flex as well, or the stretched panel keeps its content at the top
   and leaves the extra height as a gap under the border. */
.panel :deep(.p-card-body) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;
    gap: 0;
}

.panel :deep(.p-card-caption) {
    gap: 0;
}

.panel :deep(.p-card-content) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

/* Icon, title and the mode toggle share the first row; the caption takes a second row that runs
   the full width, passing under the toggle. */
.panel-head {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    /* The title row holds its old height — the one the icon used to force — so centring the icon
       across both rows does not shrink the header. */
    grid-template-rows: minmax(2.5rem, auto) auto;
    align-items: center;
    column-gap: 0.75rem;
    row-gap: 0.2rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--black-50);
    background: linear-gradient(180deg, #F7F9FD 0%, #FFFFFF 100%);
}

.panel-head-title {
    grid-column: 2;
    grid-row: 1;
}

.panel-head-caption {
    grid-column: 2 / -1;
    grid-row: 2;
}

/* Spans the title and caption rows so it sits centred against the pair, not against the title
   alone. `span 2` rather than `1 / -1`, which would stop at the end of the explicit grid. */
.panel-icon {
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 10px;
    background-color: var(--accent-100);
    color: var(--accent-700);
}

.panel-icon i {
    font-size: 1.05rem;
}

/* ---------- Upload mode toggle ---------- */

/* Sits at the end of the card header and stays there — the two modes' titles and captions are
   different lengths, and the toggle should not move with them. */
.mode-toggle {
    grid-column: 3;
    grid-row: 1;
    align-self: start;
    justify-self: end;
    display: flex;
    flex-shrink: 0;
    gap: 0.05rem;
    padding: 0.2rem;
    border: 1px solid var(--black-50);
    border-radius: 999px;
    background-color: var(--accent-100, #F1F3F7);
}

.mode-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.5rem;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: var(--gray-600);
    font-size: 0.65rem;
    font-weight: 500;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.mode-btn i {
    font-size: 0.6rem;
}

.mode-btn:hover:not(.is-active) {
    color: var(--gray-800, #1F2937);
}

.mode-btn.is-active {
    background-color: #fff;
    color: var(--accent-700);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}

/* Stands in for the fields bulk mode hides, so the two modes read as the same form. */
.mode-note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
    border: 1px dashed var(--black-50);
    border-radius: 8px;
    background-color: #F7F9FD;
    color: var(--gray-600);
    font-size: 0.75rem;
    line-height: 1.45;
}

.mode-note i {
    margin-top: 0.1rem;
    color: var(--accent-700);
}

/* The grid stretches both columns, so the card only shrinks when they stack. This keeps it steady
   there too, so toggling modes never moves the button. */
.upload-panel .panel-body {
    min-height: 32rem;
}

@media (max-width: 600px) {
    .upload-panel .panel-body {
        min-height: 0;
    }

    .mode-toggle {
        margin-left: 0;
        width: 100%;
    }

    .mode-btn {
        flex: 1;
        justify-content: center;
    }
}

.panel-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-height: 0;
    padding: 1.25rem;
}

.panel-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: auto;
    padding-top: 1rem;
}

.upload-summary {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    color: var(--black-700);
}

.upload-summary.is-blocking {
    color: #92400E;
}

.create-button {
    flex-shrink: 0;
    min-width: 10rem;
}

@media (max-width: 480px) {
    .panel-footer {
        flex-direction: column;
        align-items: stretch;
    }

    .create-button {
        width: 100%;
    }
}

/* ---------- Form sections ---------- */

.form-section {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.section-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.section-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--gray-600);
}

.section-hint {
    margin-left: auto;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    background-color: var(--gray-200);
    font-size: 0.68rem;
    color: var(--black-700);
}

.field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem 1rem;
}

.field-wide {
    grid-column: 1 / -1;
}

@media (max-width: 640px) {
    .field-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .field-wide {
        grid-column: auto;
    }
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    min-width: 0;
}

.field-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
}

.optional-hint {
    margin-left: 0.15rem;
    padding: 0.05rem 0.35rem;
    border-radius: 999px;
    background-color: var(--gray-200);
    font-size: 0.65rem;
    font-weight: 400;
    color: var(--black-700);
}

.required-hint {
    margin-left: 0.15rem;
    padding: 0.05rem 0.35rem;
    border-radius: 999px;
    background-color: var(--accent-50);
    border: 1px solid var(--accent-100);
    font-size: 0.65rem;
    font-weight: 500;
    color: #3D5A99;
}

.field-hint {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.7rem;
    line-height: 1.4;
    color: var(--black-700);
}

.field-hint i {
    font-size: 0.68rem;
}

.field :deep(.p-select) {
    width: 100%;
}

/* ---------- Upload drop zones ---------- */

.drop-stack {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
}

.drop-block {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.drop-block-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}

.drop-block-title {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
}
</style>
